import React from 'react';
import { useSelector } from 'react-redux';
import { FormikBag } from 'formik';

import { ApplicationState } from 'store/store';
import { isDefined, isString, isUndefined } from 'lib/typeguards';
import { formatDecimalToPercentage, roundToDecimal } from 'lib/helpers';
import { AwardRedemptionFormData } from 'components/perks/award-enrichments/forms/AwardRedemptionForm';
import { AwardRedemptionFullFormData } from 'components/perks/forms/RedemptionForm/PerkRedemptionForm';
import { generateApiScopeMatchCodes, generateGroupedScopeMatchCodes } from 'components/perks/helpers';
import { PerkFormStep } from 'components/perks/PerksEditStepper';
import { EPointOfSaleProvider } from 'components/settings/PointOfSaleSettings';
import { PerkDiscountCombinationRule } from 'components/loyalty/models/PerkModel';
import { AwardTemplate } from 'components/customers/models/Award';

const generateDateOptions = (initialData?: AwardRedemptionFormData) => {
    if (!initialData?.dateTimeRestriction) {
        return [];
    }
    const dateFields = [];
    if (initialData?.dateTimeRestriction?.startDate) {
        dateFields.push('date');
    }
    if (initialData?.dateTimeRestriction?.startTime) {
        dateFields.push('time');
    }
    if (
        initialData?.dateTimeRestriction?.daysOfWeek &&
        initialData?.dateTimeRestriction?.daysOfWeek?.length
    ) {
        dateFields.push('day');
    }
    return dateFields;
};

const generateToggleOptions = (initialData?: AwardRedemptionFormData) => {
    if (!initialData?.basketQualification) {
        return [];
    }
    const basketQualificationFields = [];
    if (isDefined(initialData.basketQualification.minimumValue)) {
        basketQualificationFields.push('basketQualification.minimumValue');
    }

    if (isDefined(initialData.basketQualification.minimumItems)) {
        basketQualificationFields.push('basketQualification.minimumItems');
    }
    return basketQualificationFields;
};

const transformSubmitData = (
    { dateTimeRestriction, ...data }: AwardRedemptionFullFormData,
    isIbsAndAuto: boolean,
    perkType: string
) => {
    const { dateTimeRestictionOption: _, ...dateTime } = dateTimeRestriction;
    if (isIbsAndAuto) {
        return {
            ...data,
            dateTimeRestriction: {
                ...dateTime,
                daysOfWeek: dateTime.daysOfWeek ? dateTime.daysOfWeek : []
            }
        };
    }

    const scopeMatchCode =
        data.discount.scope === 'BASKET'
            ? undefined
            : generateApiScopeMatchCodes(data.discount.groupedScopeMatchCodes);

    const isPseudoCurrency = perkType === AwardTemplate.PSEUDO_CURRENCY;
    let matchCode;
    if (!isPseudoCurrency && isString(data.basketQualification?.matchCode)) {
        matchCode = data.basketQualification.matchCode
            .split(',')
            .map(item => item.trim())
            .filter(Boolean);
    }

    const combinationRule =
        (perkType === AwardTemplate.CLUB || perkType === AwardTemplate.AD_HOC) &&
        data.discount.allowQualifierDiscount
            ? PerkDiscountCombinationRule.OWN_QUALIFIER
            : undefined;

    return {
        ...data,
        basketQualification: !data.basketQualification?.disableBasketQualification
            ? {
                  ...(data.basketQualification ?? {}),
                  matchCode
              }
            : undefined,
        discount: {
            ...data.discount,
            scopeMatchCode,
            value:
                data.discount.type === 'PERCENTAGE'
                    ? roundToDecimal(data.discount.value / 100, 4)
                    : data.discount.value,
            rule: data.discount.scope === 'BASKET' ? 'ALL_ALLOCATE' : data.discount.rule,
            combinationRule
        },
        dateTimeRestriction: {
            ...dateTime,
            daysOfWeek: dateTime.daysOfWeek ? dateTime.daysOfWeek : []
        }
    };
};

type IProps = {
    initialData?: AwardRedemptionFormData;
    perkType: string;
    auto: boolean;
    onUpdate?: (values: { type: PerkFormStep.REDEMPTION; values: AwardRedemptionFormData }) => void;
    onSubmit: (data: AwardRedemptionFormData) => void;
};

export const usePepperPerksRedemptionFormData = ({
    initialData,
    onSubmit,
    onUpdate,
    perkType,
    auto
}: IProps) => {
    const { settings } = useSelector((state: ApplicationState) => state.settings);

    const [toggledFields, setToggledFields] = React.useState<string[]>(generateToggleOptions(initialData));

    const posProvider = React.useMemo(() => settings?.posProvider, [settings]);

    const isIbsAndAuto = React.useMemo(
        () => posProvider === EPointOfSaleProvider.IBS && auto,
        [posProvider, auto]
    );

    const initialValues = React.useMemo(() => {
        if (initialData) {
            if (isIbsAndAuto) {
                return {
                    ...initialData,
                    dateTimeRestriction: {
                        ...(initialData.dateTimeRestriction || {}),
                        dateTimeRestictionOption: generateDateOptions(initialData)
                    }
                };
            }
            const dateTime = {
                ...(initialData.dateTimeRestriction ?? {}),
                dateTimeRestictionOption: generateDateOptions(initialData)
            };
            const groupedScopeMatchCodes = initialData.discount?.scopeMatchCode
                ? generateGroupedScopeMatchCodes(initialData.discount?.scopeMatchCode)
                : [''];
            const matchCode = Array.isArray(initialData.basketQualification?.matchCode)
                ? initialData.basketQualification.matchCode.join(',')
                : initialData.basketQualification?.matchCode;
            const disableBasketQualification = isDefined(initialData.basketQualification)
                ? Object.values(initialData.basketQualification).every(isUndefined)
                : true;
            const allowQualifierDiscount =
                initialData.discount?.combinationRule === PerkDiscountCombinationRule.OWN_QUALIFIER || false;
            if (initialData.discount && initialData.discount.type === 'PERCENTAGE') {
                return {
                    ...initialData,
                    basketQualification: {
                        ...(initialData.basketQualification ?? {}),
                        matchCode,
                        disableBasketQualification
                    },
                    discount: {
                        ...initialData.discount,
                        value: formatDecimalToPercentage(initialData.discount.value),
                        groupedScopeMatchCodes,
                        maxCount: initialData.discount.maxCount || 1,
                        allowQualifierDiscount
                    },
                    dateTimeRestriction: dateTime
                };
            }
            return {
                ...initialData,
                basketQualification: {
                    ...(initialData.basketQualification ?? {}),
                    matchCode,
                    disableBasketQualification
                },
                discount: {
                    ...initialData.discount,
                    groupedScopeMatchCodes,
                    maxCount: initialData.discount.maxCount || 1,
                    allowQualifierDiscount
                },
                dateTimeRestriction: dateTime
            };
        }
    }, [initialData, isIbsAndAuto]);

    React.useEffect(() => {
        if (isIbsAndAuto) {
            return;
        }
        if (initialData?.basketQualification) {
            setToggledFields(generateToggleOptions(initialData));
        }
    }, [initialData, isIbsAndAuto]);

    const handleSubmit = React.useCallback(
        (data: AwardRedemptionFullFormData, formikBag: FormikBag<any, AwardRedemptionFullFormData>) => {
            const transformedData = transformSubmitData(data, isIbsAndAuto, perkType);
            onSubmit(transformedData);
            formikBag.setSubmitting(false);
        },
        [perkType, isIbsAndAuto, onSubmit]
    );

    const handleUpdate = React.useCallback(
        (data: AwardRedemptionFullFormData) => {
            const transformedData = transformSubmitData(data, isIbsAndAuto, perkType);
            onUpdate({
                type: PerkFormStep.REDEMPTION,
                values: transformedData
            });
        },
        [perkType, isIbsAndAuto, onUpdate]
    );

    return {
        toggledFields,
        setToggledFields,
        posProvider,
        initialValues,
        handleSubmit,
        handleUpdate,
        isIbsAndAuto
    };
};
